import Link from 'next/link';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, IconButton, Stack, styled, Typography } from '@/primitives';
import { footerMessages } from './messages';
import { PUBLIC_ROUTES } from '@/configs/routes';
import { FOOTER_HEIGHT, MOBILE_FOOTER_HEIGHT } from '@/configs/layout';
import { HealthStatusChip } from './HealthStatusChip';
import ColorSchemeToggle from './toolbar/ColorSchemeToggle';

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  height: 26px;
  padding: 2px 4px;
  margin-top: 0 !important;
  text-transform: capitalize;
`;

/** Shared MUI `sx` for all footer link labels. */
const footerLinkTypographySx = {
  fontSize: '11px',
  fontWeight: { xs: 400, sm: 500 },
  letterSpacing: { xs: '0.1px', sm: '0.5px' },
} as const;

interface ShellFooterProps {
  /** When true, footer is fixed to the viewport bottom and does not scroll with the page */
  fixed?: boolean;
}

export default function ShellFooter({ fixed = false }: ShellFooterProps) {
  return (
    <Stack
      component="footer"
      sx={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: { xs: '8px', md: '24px' },
        borderTop: 1,
        borderColor: 'divider',
        minHeight: { xs: MOBILE_FOOTER_HEIGHT, md: FOOTER_HEIGHT },
        bgcolor: 'background.paper',
        zIndex: 1300,
        ...(fixed && {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
        }),

        '@media print': {
          display: 'none',
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: { xs: '4px', sm: '12px', md: '24px' },
        }}
      >
        <StyledLink href={PUBLIC_ROUTES.HOME}>
          <Typography
            component="span"
            sx={[
              {
                color: 'text.secondary',
              },
              ...(Array.isArray(footerLinkTypographySx)
                ? footerLinkTypographySx
                : [footerLinkTypographySx]),
            ]}
          >
            © {new Date().getFullYear()} ABRI
          </Typography>
        </StyledLink>
        <Stack
          direction="row"
          sx={{
            gap: 0,
          }}
        >
          <StyledLink
            href={PUBLIC_ROUTES.TERMS}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography
              component="span"
              sx={[
                {
                  color: 'text.secondary',
                },
                ...(Array.isArray(footerLinkTypographySx)
                  ? footerLinkTypographySx
                  : [footerLinkTypographySx]),
              ]}
            >
              {footerMessages['footer.terms.label']}
            </Typography>
          </StyledLink>
          <StyledLink
            href={PUBLIC_ROUTES.PRIVACY}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography
              component="span"
              sx={[
                {
                  color: 'text.secondary',
                },
                ...(Array.isArray(footerLinkTypographySx)
                  ? footerLinkTypographySx
                  : [footerLinkTypographySx]),
              ]}
            >
              {footerMessages['footer.privacy.label']}
            </Typography>
          </StyledLink>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: { xs: '4px', sm: '8px' },
        }}
      >
        <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
          <HealthStatusChip />
        </Box>
        <ColorSchemeToggle layout="footer" />
        <StyledLink href={PUBLIC_ROUTES.SUPPORT} target="_blank">
          <Typography
            component="span"
            sx={{
              color: 'text.primary',
              ...footerLinkTypographySx,
              whiteSpace: 'nowrap',
            }}
          >
            {footerMessages['footer.support.label']}
          </Typography>
        </StyledLink>
        <IconButton
          aria-label="Scroll to top"
          sx={{
            color: 'text.secondary',
            height: 26,
            width: 26,
            padding: '4px',
          }}
          onClick={() => {
            document
              .getElementById('main')
              ?.scrollTo({ top: 0, behavior: 'smooth' });
            document
              .getElementById('shell')
              ?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: '18px' }} />
        </IconButton>
      </Stack>
    </Stack>
  );
}
